import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox/Checkbox";
import { ClickableProps } from "@insite/mobius/Clickable/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getdiscrepancyCollection } from "@tKgpco/Services/DiscrepancyService";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import loadDiscrepancyCollection from "@tKgpco/Store/Data/DiscrepancyHistory/Handlers/loadDiscrepancyCollection";
import React, { use } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => ({
    discrepancyCollection: state.KGPState.DiscrepancyStore.discrepancyCollection?.discrepancyCollectionProperties,
    discrepancyLoading: state.KGPState.DiscrepancyStore.isLoading,
    language: state.context.session.language,
    discrepanyparams: state.KGPState.DiscrepancyStore.discrepancyCollectionParams,
    showPricing:
        state.context.showPricing && state.context?.session?.properties?.canShowPrice?.toLowerCase() === "true",
});

type Props = ReturnType<typeof mapStateToProps> & WidgetProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapDispatchToProps = {
    loadDiscrepancyCollection,
};

export interface DiscrepancyHistoryTableStyles {
    container?: InjectableCss;
    headerClickable?: ClickableProps;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    orderDateHeader?: DataTableHeaderProps;
    orderNumberHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    customerPOHeader?: DataTableHeaderProps;
    orderTotalHeader?: DataTableHeaderProps;
    reorderHeader?: DataTableHeaderProps;
    orderDateCells?: DataTableCellProps;
    orderNumberCells?: DataTableCellProps;
    shipToCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    customerPOCells?: DataTableCellProps;
    orderTotalCells?: DataTableCellProps;
    reorderCells?: DataTableCellProps;
    reorderButton?: ButtonPresentationProps;
    reorderButtonSpinner?: LoadingSpinnerProps;
    orderNumberLink?: LinkPresentationProps;
    dataTable?: DataTableProps;
    checkboxHeader?: DataTableHeaderProps;
    checkboxCells?: DataTableCellProps;
    rowCheckbox?: CheckboxPresentationProps;
    headerContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    exportLink?: LinkPresentationProps;
    tableContainer?: InjectableCss;
}

export const discrepancyHistoryTableStyles: DiscrepancyHistoryTableStyles = {
    container: {
        css: css`
            overflow: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
    headerClickable: {
        css: css`
            width: 100%;
            justify-content: space-between;
        `,
    },
    reorderButton: {
        color: "secondary",
        sizeVariant: "medium",
    },
    reorderButtonSpinner: {
        size: 22,
        css: css`
            margin-left: auto;
            margin-right: auto;
            display: block;
        `,
    },
    shipToHeader: {
        tight: true,
    },
    orderTotalHeader: {
        tight: true,
        alignX: "right",
    },
    reorderHeader: {
        tight: true,
    },
    orderTotalCells: {
        alignX: "right",
    },
    shipToCells: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 300px;
            `,
        },
    },
    checkboxHeader: {
        tight: true,
    },
    headerContainer: {
        gap: 8,
        css: css`
            padding: 20px 0;
        `,
    },
    gridItem: {
        width: 12,
        css: css`
            > * {
                padding-right: 10px;
            }
        `,
    },
    exportLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    tableContainer: {
        css: css`
            border-radius: 10px 0;
            width: 100%;
            overflow: auto;
        `,
    },
};

const styles = discrepancyHistoryTableStyles;

type sortByField = {
    [key: string]: string[];
};

const sortByFields: sortByField = {
    customerPartNumber: ["customerPartNumber"],
    date: ["date"],
    discrepancyNumber: ["discrepancyNumber"],
    orderTotal: ["orderTotal"],
    poLineNumber: ["poLineNumber"],
    poNumber: ["poNumber"],
    status: ["status"],
    supplierPartNumber: ["supplierPartNumber"],
    unitPrice: ["unitPrice"],
    assignedTo: ["assignedTo"],
};

const DiscrepancyHistoryTable: React.FC<Props> = ({
    discrepancyCollection,
    discrepancyLoading,
    language,
    loadDiscrepancyCollection,
    discrepanyparams,
    showPricing,
}) => {
    if (discrepancyLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (discrepancyCollection?.length === 0 || !discrepancyCollection) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography as="p" {...styles.noResultsText} data-test-selector="orderHistoryTable_noOrdersFound">
                    {translate("No Discrepancies found")}
                </Typography>
            </StyledWrapper>
        );
    }

    const changeSort = (sort: string) => {
        loadDiscrepancyCollection({
            ...discrepanyparams,
            sort: discrepanyparams?.sort === sort ? `${sort} DESC` : sort,
        });
    };

    const headerClick = (sortField: string) => {
        const previousSort = discrepanyparams?.sort ?? "";
        const [previousPrimaryField] = previousSort.split(",");
        const direction = previousPrimaryField === sortField ? " DESC" : "";
        const sort = sortByFields[sortField].map(field => `${field}${direction}`).join(", ");
        loadDiscrepancyCollection({
            ...discrepanyparams,
            sort,
        });
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (!discrepanyparams?.sort) {
            return sorted;
        }
        const [primaryField] = discrepanyparams.sort.split(",");
        if (primaryField === sortField) {
            sorted = "ascending";
        } else if (primaryField === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.tableContainer}>
                {discrepancyCollection?.length ? (
                    <DataTable {...styles.dataTable}>
                        <DataTableHead>
                            <DataTableHeader
                                className="col-date"
                                sorted={sorted("discrepancyNumber")}
                                onSortClick={() => headerClick("discrepancyNumber")}
                                data-test-selector="tableHeader_DiscrepancyNumber"
                            >
                                {translate("Discrepancy #")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-date"
                                sorted={sorted("date")}
                                onSortClick={() => headerClick("date")}
                                data-test-selector="tableHeader_Date"
                            >
                                {translate("Date")}
                            </DataTableHeader>
                            {showPricing && (
                                <DataTableHeader
                                    className="col-shipto"
                                    sorted={sorted("unitPrice")}
                                    onSortClick={() => headerClick("unitPrice")}
                                    data-test-selector="tableHeader_UnitPrice"
                                >
                                    {translate("Unit Price")}
                                </DataTableHeader>
                            )}
                            <DataTableHeader
                                className="col-date"
                                sorted={sorted("status")}
                                onSortClick={() => headerClick("status")}
                                data-test-selector="tableHeader_Status"
                            >
                                {translate("Status")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                sorted={sorted("poLineNumber")}
                                onSortClick={() => headerClick("poLineNumber")}
                                data-test-selector="tableHeader_POLine"
                            >
                                {translate("PO Line")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                sorted={sorted("supplierPartNumber")}
                                onSortClick={() => headerClick("supplierPartNumber")}
                                data-test-selector="tableHeader_SupplierPartNumber"
                            >
                                {translate("Supplier Part #")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                sorted={sorted("customerPartNumber")}
                                onSortClick={() => headerClick("customerPartNumber")}
                                data-test-selector="tableHeader_CustomerPartNumber"
                            >
                                {translate("Customer Part #")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                sorted={sorted("poNumber")}
                                onSortClick={() => headerClick("poNumber")}
                                data-test-selector="tableHeader_PONumber"
                            >
                                {translate("PO #")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                sorted={sorted("assignedTo")}
                                onSortClick={() => headerClick("assignedTo")}
                                data-test-selector="tableHeader_AssignedTo"
                            >
                                {translate("Assigned To")}
                            </DataTableHeader>
                        </DataTableHead>
                        <DataTableBody>
                            {discrepancyCollection?.map(discrepancy => (
                                <DataTableRow key={discrepancy.discrepancyNumber}>
                                    <DataTableCell className="col-shipto">
                                        {discrepancy.discrepancyNumber ? (
                                            <Link
                                                href={`/MyAccount/DiscrepancyHistory/DiscrepancyDetails?discrepancyId=${discrepancy.discrepancyNumber}`}
                                                {...styles.orderNumberLink}
                                            >
                                                {discrepancy.discrepancyNumber}
                                            </Link>
                                        ) : null}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {discrepancy.date
                                            ? getLocalizedDateTime({
                                                  dateTime: new Date(discrepancy.date),
                                                  language,
                                              })
                                            : ""}
                                    </DataTableCell>
                                    {showPricing && (
                                        <DataTableCell className="col-shipto">{discrepancy.unitPrice}</DataTableCell>
                                    )}
                                    <DataTableCell className="col-shipto">{discrepancy.status}</DataTableCell>
                                    <DataTableCell className="col-shipto">{discrepancy.poLineNumber}</DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {discrepancy.supplierPartNumber}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {discrepancy.customerPartNumber}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">{discrepancy.poNumber}</DataTableCell>
                                    <DataTableCell className="col-shipto">{discrepancy.assignedTo}</DataTableCell>
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                ) : null}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(DiscrepancyHistoryTable)),
    definition: {
        group: "Common",
        displayName: "DiscrepancyHistoryTable",
    },
};

export default widgetModule;
